import React, { ReactElement, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import ErrorMessage from '@empiriecom/module-components/FormControls/Input/ErrorMessage';
import { formTheme } from '@empiriecom/module-components/FormControls/theme';
import { ComponentThemeProvider } from '@empiriecom/module-components/ThemeProvider';
import Button from '@empiriecom/module-components/Button';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import { RatingIcon } from '@/src/components/icons';
import { FragmentConfiguration } from '@/config';
import { useCreateRatingContext } from '../CreateRatingContext';
import CreateRatingFormInput from './CreateRatingInput';

const RatingStarsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin: 1.75rem auto;
`;

const RatingStarsWrapper = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
`;

const RatingStar = styled(RatingIcon)`
  transition: fill 0.3s ease-out;
`;

const RatingStarsHint = styled.div`
  font-size: 0.75rem;
  color: #a0a0a0;
  margin-top: 0.25rem;
`;

const RatingValueError = styled.div`
  margin-top: 0.5rem;
`;

const RatingFormContainer = styled.div``;

const MandatoryFieldsHint = styled.div<{ mobile: boolean }>`
  color: #a0a0a0;
  font-size: 0.75rem;
  ${({ mobile }) => mobile && 'margin-top: 1rem;'}
`;

const SubmitButtonWrapper = styled.div<{ mobile: boolean }>`
  margin-top: 2.5rem;
  display: flex;
  justify-content: ${({ mobile }) => (mobile ? 'center' : 'flex-end')};
`;

const handleRatingValueChange = (setRatingValue: (value: number) => void, ratingValue: number) => {
  setRatingValue(ratingValue);
};

export const CreateRatingForm = (): ReactElement => {
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const config: FragmentConfiguration = useConfig();
  const {
    setRatingValue,
    getRatingValue,
    setHoveredRating,
    getHoveredRating,
    getRatingReview,
    getReviewerName,
    getTermsAccepted,
    setTermsAccepted,
    setCreationStep,
  } = useCreateRatingContext();

  useEffect(() => setTermsAccepted(null), []);

  useEffect(() => {
    if (
      getRatingReview() !== '' &&
      getReviewerName() !== '' &&
      getRatingValue() >= 1 &&
      getTermsAccepted()
    ) {
      setIsFormValid(true);
    } else {
      setIsFormValid(false);
    }
  });

  return (
    <div data-testid="create-rating-form">
      <RatingStarsContainer>
        <RatingStarsWrapper onMouseLeave={() => setHoveredRating(0)}>
          {config.customerRatings.formConfig.starValues.map((value: number) => (
            <RatingStar
              data-testid="rating-star"
              key={value}
              size="2rem"
              onClick={() => handleRatingValueChange(setRatingValue, value)}
              onMouseOver={() => setHoveredRating(value)}
              onFocus={() => setHoveredRating(value)}
              fill={
                getHoveredRating() >= value || getRatingValue() >= value
                  ? config.customerRatings.iconColor
                  : '#d5d3d1'
              }
            />
          ))}
        </RatingStarsWrapper>
        <RatingStarsHint>
          <FormattedMessage
            id="ratings.new-rating.stars-hint"
            defaultMessage="1 (mangelhaft) - 5 (super)*"
          />
        </RatingStarsHint>
        {getRatingValue() !== null && getRatingValue() < 1 && (
          <ComponentThemeProvider theme={formTheme}>
            <RatingValueError>
              <ErrorMessage
                error={
                  <FormattedMessage
                    id="ratings.new-rating.rating-value-error"
                    defaultMessage="Du musst einen Wert für die Gesamtbewertung eingeben."
                  />
                }
              />
            </RatingValueError>
          </ComponentThemeProvider>
        )}
      </RatingStarsContainer>
      <RatingFormContainer>
        {config.customerRatings.formConfig.fields.map((field, index) => (
          <CreateRatingFormInput field={field} key={field.name.concat(index.toFixed())} />
        ))}
      </RatingFormContainer>
      <MandatoryFieldsHint mobile={isMobile}>
        <FormattedMessage
          id="rating.new-rating.mandatory-fields-hint"
          defaultMessage="Die mit * markierten Felder sind Pflichtfelder."
        />
      </MandatoryFieldsHint>
      <SubmitButtonWrapper mobile={isMobile}>
        <Button
          layout="primary"
          disabled={!isFormValid}
          onClick={() => setCreationStep('PREVIEW')}
          uppercase={false}
          data-testid="submit-preview-button"
        >
          <FormattedMessage
            id="ratings.new-rating.show-rating-preview"
            defaultMessage="Weiter zur Vorschau"
          />
        </Button>
      </SubmitButtonWrapper>
    </div>
  );
};

export default CreateRatingForm;
